/* eslint-disable class-methods-use-this */
import * as Constants from 'client/util/Constants';
import { CancelToken } from 'axios';
import {
  assignIn,
  cloneDeep,
  filter,
} from 'lodash';
import CloudInsightsConstants, { csvData, flattenObject } from 'client/CloudInsights/constants/CloudInsightsConstants';
import CloudInsightsEventsApi from 'client/api/v2/CloudInsightsEventsApi';
import CloudInsightsUtil from 'client/CloudInsights/util/CloudInsightsUtil';
import CollectionModule from 'client/store/modules/CollectionModule';
import DateUtil from 'client/util/DateUtil';
import DownloadUtil from 'client/util/DownloadUtil';
import JobUtils from 'client/util/JobUtils';
import ModalEventBus from 'components/Modal/ModalEventBus';
import Vue from 'vue';
import Vuex from 'vuex';
import moment from 'moment';

Vue.use(Vuex);

const displayMaxExportWarning = () => {
  ModalEventBus.open({
    component: 'ConfirmationModal',
    componentProps: {
      bodyText: 'The export limit has been reached (100,000 events). Please adjust time range to export more.',
      cancelText: '',
      continueText: 'okay',
      headerText: 'Export Exceeded Maximum Limit',
    },
  });
};

const { cloudInsightsColumns } = CloudInsightsConstants;
const { ExportFileTypes } = Constants;
const { dataNotAvailable } = Constants.default;

export class CloudInsightsEventsCollection extends CollectionModule {
  constructor() {
    super(CloudInsightsEventsApi, '_id');
  }

  get module() {
    const defaultModule = super.extractModule();

    assignIn(defaultModule.state, {
      isLoading: false,
      nonPersonEntityFilter: false,
      filterOptions: {},
      savedViews: [],
      selectedSavedView: '',
    });

    assignIn(defaultModule.getters, {
      getDateTimeRange: this.getDateTimeRange.bind(this),
    });

    assignIn(defaultModule.actions, {
      addFilter: this.addFilter.bind(this),
      cancelExport: this.cancelExport.bind(this),
      deleteFilter: this.deleteFilter.bind(this),
      deleteSavedView: this.deleteSavedView.bind(this),
      exportData: this.exportData.bind(this),
      fetchCollection: this.fetchCollection.bind(this),
      fetchDistinctValues: this.fetchDistinctValues.bind(this),
      fetchEventsData: this.fetchEventsData.bind(this),
      fetchSavedViews: this.fetchSavedViews.bind(this),
      saveView: this.saveView.bind(this),
      setDateTimeRange: this.setDateTimeRange.bind(this),
      setFilter: this.setFilter.bind(this),
      setFilters: this.setFilters.bind(this),
      setLoadingState: this.setLoadingState.bind(this),
      setNonPersonEntityFilter: this.setNonPersonEntityFilter.bind(this),
      setSelectedSavedView: this.setSelectedSavedView.bind(this),
      resetFilters: this.resetFilters.bind(this),
      updateSavedView: this.updateSavedView.bind(this),
    });

    assignIn(defaultModule.mutations, {
      ADD_FILTER: this.ADD_FILTER,
      DELETE_FILTER: this.DELETE_FILTER,
      DELETE_SAVED_VIEW: this.DELETE_SAVED_VIEW,
      RESET_FILTERS: this.RESET_FILTERS,
      SET_CANCEL_EXPORT: this.SET_CANCEL_EXPORT,
      SET_DATE_TIME_RANGE: this.SET_DATE_TIME_RANGE,
      SET_EXPORT_EVENTS: this.SET_EXPORT_EVENTS,
      SET_FILTER: this.SET_FILTER,
      SET_FILTER_OPTIONS: this.SET_FILTER_OPTIONS,
      SET_LOADING_STATE: this.SET_LOADING_STATE,
      SET_NPE_FILTER: this.SET_NPE_FILTER,
      SET_SAVED_VIEW: this.SET_SAVED_VIEW,
      SET_SAVED_VIEWS: this.SET_SAVED_VIEWS,
      SET_SELECTED_SAVED_VIEW: this.SET_SELECTED_SAVED_VIEW,
      UPDATE_SAVED_VIEW: this.UPDATE_SAVED_VIEW,
    });

    return defaultModule;
  }

  /**
   * Exports all Cloud Insights events data that satisfies the current filters chosen.
   * Only exports a maximum of 100k rows
   */
  exportData({ commit, dispatch, state }, exportType = ExportFileTypes.json) {
    let data = [];
    // The max size of events to receive per request.
    // Warning: Setting this at 4k can cause the following error if the results are too large:
    //  middlewareError - Error: 8 RESOURCE_EXHAUSTED: Received message larger than
    //  max (5252157 vs. 4194304) status_code=500
    const rowBatchSize = 2000;
    // Set total number of events for progress module status bar.
    const totalNumRows = state.entities.items.totalCount;
    const maxLimit = 1e5;
    let numRowsFetched = 0;

    const completeProgressCounter = () => {
      dispatch('ProgressModule/setCounter', { completed: totalNumRows }, { root: true });
    };

    const collectData = (searchAfter) => {
      const payload = { limit: rowBatchSize };
      if (searchAfter) payload.search_after = searchAfter;

      return dispatch('fetchEventsData', payload)
        .then(async (response) => {
          data = data.concat(response.data);
          numRowsFetched += response.resultCount;

          if (state.cancelExport) {
            completeProgressCounter();
            // Reset cancel export
            commit('SET_CANCEL_EXPORT', false);
            throw new Error('Events export cancelled');
          }

          // If response indicates there are no more rows to load, stop
          if (response.resultCount < response.limit) {
            completeProgressCounter(); // This is to guarantee that progress modal completes
            return;
          }

          // Stop fetching after hitting max limit
          if (numRowsFetched >= maxLimit) {
            completeProgressCounter();
            displayMaxExportWarning();
            return;
          }

          // Using a fake number of total rows, so fake completed count if it goes over
          const completed = (numRowsFetched >= totalNumRows)
            ? totalNumRows - 1000
            : numRowsFetched;
          dispatch('ProgressModule/setCounter', {
            completed,
            total: totalNumRows,
          }, { root: true });

          // Continue to gather data while API indicates there are still more to load
          await collectData(response.searchAfter);
        });
    };

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      if (exportType !== ExportFileTypes.json && exportType !== ExportFileTypes.csv) {
        return reject(new Error('Invalid file type'));
      }

      try {
        // First batch of events take a few seconds, so show the progress module right away
        dispatch('ProgressModule/setCounter', {
          completed: 0,
          total: totalNumRows,
        }, { root: true });
        await collectData();
      } catch (err) {
        completeProgressCounter();
        return reject(err);
      }

      /* eslint-disable no-param-reassign */
      if (exportType === ExportFileTypes.csv) {
        // Flatten nested object
        data = data.reduce((acc, obj) => {
          const flat = flattenObject(obj);
          const formattedCsvRow = csvData.reduce((acc2, obj2) => {
            if (obj2.dataFieldName === cloudInsightsColumns.eventTime) {
              const date = DateUtil.formatUnixDate(flat['eventTime.seconds']);
              const time = DateUtil.formatUnixTime(flat['eventTime.seconds']);
              acc2[obj2.label] = `${date} ${time}`;
              return acc2;
            }
            if (obj2.dataFieldName === cloudInsightsColumns.connector) {
              let connector = dataNotAvailable;
              if (flat.jumpcloudUser) {
                connector = 'IAM';
              }
              if (flat.ssoUser) {
                connector = 'SSO';
              }
              // eslint-disable-next-line no-param-reassign
              acc2[obj2.label] = connector;
              return acc2;
            }
            if (obj2.getJson) {
              const json = obj2.getJson(obj);
              // eslint-disable-next-line no-param-reassign
              acc2[obj2.label] = json;
              return acc2;
            }
            // eslint-disable-next-line no-param-reassign
            acc2[obj2.label] = flat[obj2.dataFieldName];
            return acc2;
          }, {});
          acc.push(formattedCsvRow);
          return acc;
        }, []);
      }
      /* eslint-enable no-param-reassign */

      const dateTimeFormat = 'YYYYMMDD_hh_mm_ss_a';
      const { start, end } = state.dateTimeRange;
      const startDate = moment(start).format(dateTimeFormat);
      const endDate = end
        ? moment(end).format(dateTimeFormat)
        : moment(new Date()).format(dateTimeFormat);
      let fileName = `cloudInsightsEvents_${startDate}`;
      if (endDate !== startDate) fileName += `_${endDate}`;

      if (exportType === ExportFileTypes.json) {
        const eventsData = data.map(event => CloudInsightsUtil.sanitizeForDisplay(event));
        DownloadUtil.downloadJson(eventsData, `${fileName}.json`);
      } else if (exportType === ExportFileTypes.csv) {
        const csv = JobUtils.convertJsonToCsv(data);
        DownloadUtil.downloadFile(csv, `${fileName}.csv`);
      }
      return resolve(numRowsFetched);
    });
  }

  getInitialState() {
    const state = super.getInitialState();

    // When called by extractModule(), dateTimeRange set with initial values on page load.
    // When called during RESET_COLLECTION, these values won't be Vue.set in state. This is okay,
    //  since dateTimeRange values won't get reset when navigating to another internal route
    //  and back to cloud insights.
    const jsDate = new Date();
    state.dateTimeRange = {
      start: moment(jsDate).subtract({ hours: 1 }).format(),
      end: moment(jsDate).format(),
    };

    state.savedViews = [];

    return state;
  }

  addFilter({ commit }, field) {
    commit('ADD_FILTER', field);
  }

  cancelExport({ commit }) {
    commit('SET_CANCEL_EXPORT', true);
  }

  deleteFilter({ commit }, field) {
    commit('DELETE_FILTER', field);
  }

  getDateTimeRange(state) {
    return state.dateTimeRange;
  }

  async deleteSavedView(context, { id }) {
    await CloudInsightsEventsApi.deleteSavedView(id);

    context.commit('DELETE_SAVED_VIEW', { id });
  }

  fetchCollection(context, { next = false } = {}) {
    return new Promise((resolve, reject) => {
      let { skip } = context.state.features.items.pagination;
      const { sort } = context.state.features.items;
      const { searchTerm } = context.state.features.items;
      const {
        skipToken,
        nextPageToken,
        limit,
      } = context.state.features.items.pagination;
      const {
        dateTimeRange,
        nonPersonEntityFilter,
      } = context.state;
      const filters = context.getters.getFilters;

      const accounts = context.rootGetters['CloudInsightsAccountsCollection/getCollection'];

      const accountIds = accounts.map((account) => (account.awsAccountId));

      context.commit('SET_LOADING_STATE', true);

      if (next === true && !nextPageToken && !skipToken) {
        // nextPageToken is set to null when all results have been fetched
        // skipToken is set to null when all results have been fetched
        if (this.Api.usesPageTokens || this.Api.usesSkipTokens) {
          resolve();
          return;
        }

        skip += limit;

        if (skip >= context.state.entities.items.totalCount) {
          resolve();
          return;
        }

        context.commit('SET_PAGINATION_SKIP', { skip });
      }

      this.fetchWrapper(
        context,
        skip,
        limit,
        sort,
        searchTerm,
        filters,
        nextPageToken,
        skipToken,
        dateTimeRange,
        nonPersonEntityFilter,
        accountIds,
      )
        .then((items) => {
          const payload = {
            results: items.results,
            totalCount: items.totalCount,
            nextPageToken: items.nextPageToken,
            skipToken: items.skipToken,
          };

          if (context.state.entities.items.totalCountOfAllItems === null) {
            payload.totalCountOfAllItems = items.totalCount;
          }

          context.commit('SET_COLLECTION', payload);

          return items.results.map(result => result.id);
        })
        .then((ids) => {
          resolve(ids);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          context.commit('SET_LOADING_STATE', false);
        });
    });
  }

  fetchWrapper(
    context,
    skip,
    limit,
    sort,
    searchTerm,
    filters,
    nextPageToken,
    skipToken,
    dateTimeRange,
    nonPersonEntityFilter,
    accounts,
  ) {
    // Finally, when not showing bound items then just return the promise
    // returned from the Api instance fetch() method.

    // If there is an ongoing fetch request, cancel it
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel();
    }

    // Create and save a cancel token for the fetch request
    this.cancelTokenSource = CancelToken.source();
    const cancelToken = this.cancelTokenSource.token;

    return this.Api.fetch(
      skip,
      limit,
      sort,
      searchTerm,
      cancelToken,
      filters,
      nextPageToken,
      skipToken,
      dateTimeRange,
      nonPersonEntityFilter,
      accounts,
    );
  }

  async fetchDistinctValues(context, { field }) {
    const { dateTimeRange } = context.state;
    const { nonPersonEntityFilter } = context.state;
    const accounts = context.rootGetters['CloudInsightsAccountsCollection/getCollection'];
    const accountIds = accounts.map((account) => (account.awsAccountId));
    const { searchTerm } = context.state.features.items;

    const modifiedFilters = filter(
      context.getters.getFilters,
      (filterObj) => filterObj.field !== field,
    );

    let options = await CloudInsightsEventsApi.fetchDistinct(
      field,
      dateTimeRange,
      accountIds,
      nonPersonEntityFilter,
      searchTerm,
      modifiedFilters,
    );

    if (field === cloudInsightsColumns.connector) options = ['IAM', 'SSO', dataNotAvailable];

    context.commit('SET_FILTER_OPTIONS', { field, options });
  }

  fetchEventsData(context, payload) {
    const {
      searchTerm,
      sort,
    } = context.state.features.items;
    const {
      dateTimeRange,
      nonPersonEntityFilter,
    } = context.state;
    const filters = context.getters.getFilters;
    const accounts = context.rootGetters['CloudInsightsAccountsCollection/getCollection'];
    const accountIds = accounts.map((account) => (account.awsAccountId));
    const searchAfter = payload.search_after || undefined;
    // Don't use skip with searchAfter to be safe.
    // API will use searchAfter if skip is 0 or not provided.
    const skip = undefined;
    // These tokens are in the fetch call params, but not used at the moment
    const cancelToken = undefined;
    const nextPageToken = undefined;
    const skipToken = undefined;

    // Fetch event data using search_after instead of skip
    return CloudInsightsEventsApi.fetch(
      skip,
      payload.limit,
      sort,
      searchTerm,
      cancelToken,
      filters,
      nextPageToken,
      skipToken,
      dateTimeRange,
      nonPersonEntityFilter,
      accountIds,
      searchAfter,
    )
      .then(response => {
        context.commit('SET_EXPORT_EVENTS', response.results);
        return {
          data: response.results,
          limit: payload.limit,
          resultCount: response.results.length,
          searchAfter: response.searchAfter,
        };
      });
  }

  async fetchSavedViews(context) {
    const savedViews = await CloudInsightsEventsApi.fetchSavedViews();

    context.commit('SET_SAVED_VIEWS', { savedViews });
  }

  async saveView(context, savedView) {
    const view = await CloudInsightsEventsApi.saveView(savedView);

    context.commit('SET_SAVED_VIEW', { savedView: view });
    return view;
  }

  setDateTimeRange({ commit }, { dateTimeRange }) {
    commit('SET_DATE_TIME_RANGE', { dateTimeRange });
  }

  setFilter({ commit }, { field, values }) {
    commit('SET_FILTER', { field, values });
  }

  setFilters({ commit }, { filtersArray }) {
    commit('RESET_COLLECTION', { soft: true });
    filtersArray.forEach(({ field, values }) => {
      commit('SET_FILTER', { field, values });
    });
  }

  setLoadingState(context, isLoading) {
    context.commit('SET_LOADING_STATE', isLoading);
  }

  setNonPersonEntityFilter(context, { value }) {
    context.commit('SET_NPE_FILTER', { value });
  }

  setSelectedSavedView(context, viewId) {
    context.commit('SET_SELECTED_SAVED_VIEW', viewId);
  }

  resetFilters(context) {
    context.commit('RESET_FILTERS');
  }

  async updateSavedView(context, { savedView }) {
    const view = await CloudInsightsEventsApi.putView(savedView);

    context.commit('UPDATE_SAVED_VIEW', { updatedView: view });
    return view;
  }

  ADD_FILTER(state, field) {
    const { filters } = state.features.items;
    const filterIndex = filters.findIndex(f => f.field === field);
    if (filterIndex === -1) {
      state.features.items.filters.push({ field, values: [] });
    }
  }

  DELETE_FILTER(state, field) {
    const index = state.features.items.filters.findIndex(d => d.field === field);
    if (index > -1) {
      state.features.items.filters.splice(index, 1);
    }
  }

  DELETE_SAVED_VIEW(state, { id }) {
    const newSavedViews = [...state.savedViews];
    const viewIndex = newSavedViews.findIndex(v => v.viewId === id);
    if (viewIndex > -1) {
      newSavedViews.splice(viewIndex, 1);
      Vue.set(state, 'savedViews', newSavedViews);
    }
  }

  SET_CANCEL_EXPORT(state, cancelExport) {
    Vue.set(state, 'cancelExport', cancelExport);
  }

  SET_DATE_TIME_RANGE(state, { dateTimeRange }) {
    Vue.set(state, 'dateTimeRange', dateTimeRange);
  }

  SET_EXPORT_EVENTS(state, events) {
    Vue.set(state, 'exportEvents', events);
  }

  SET_FILTER(state, { field, values }) {
    // Replaces entirely the filter value with the provided value
    const filters = cloneDeep(state.features.items.filters);
    const existingFilters = filters.find(d => d.field === field);
    if (existingFilters) {
      existingFilters.values = values;
    } else {
      filters.push({ field, values });
    }
    Vue.set(state.features.items, 'filters', filters);
  }

  SET_FILTER_OPTIONS(state, { field, options }) {
    Vue.set(state, 'filterOptions', {
      ...state.filterOptions,
      [field]: options,
    });
  }

  SET_NPE_FILTER(state, { value }) {
    Vue.set(state, 'nonPersonEntityFilter', value);
  }

  SET_LOADING_STATE(state, isLoading) {
    Vue.set(state, 'isLoading', isLoading);
  }

  SET_SAVED_VIEWS(state, { savedViews }) {
    Vue.set(state, 'savedViews', savedViews);
  }

  SET_SAVED_VIEW(state, { savedView }) {
    const views = state.savedViews;
    views.push(savedView);
    Vue.set(state, 'savedViews', views);
  }

  SET_SELECTED_SAVED_VIEW(state, viewId) {
    Vue.set(state, 'selectedSavedView', viewId);
  }

  RESET_FILTERS(state) {
    Vue.set(state.features.items, 'filters', []);
  }

  UPDATE_SAVED_VIEW(state, { updatedView }) {
    const viewIndex = state.savedViews.findIndex(v => v.id === updatedView.viewId);
    if (viewIndex > -1) {
      Vue.set(state.savedViews, viewIndex, updatedView);
    }
  }
}

export default new CloudInsightsEventsCollection().module;
